import React, { useEffect, useState } from 'react';
import { api } from './App';
import { InfoTooltip } from './InfoTooltip';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEK_START = new Date('2026-03-30');

function getWeekDateRange(weekNum) {
  const start = new Date(WEEK_START);
  start.setDate(start.getDate() + (weekNum - 1) * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${fmt(start)} – ${fmt(end)}`;
}

function EditableTarget({ weekNum, value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const save = async () => {
    const n = Math.max(0, parseInt(draft) || 0);
    setDraft(n);
    setEditing(false);
    await api('/api/mcq/target', {
      method: 'POST',
      body: JSON.stringify({ week_number: weekNum, target: n }),
    });
    onChange(n);
  };

  if (editing) {
    return (
      <input className="mcq-target-input" type="number" min="0" max="999"
        value={draft} autoFocus
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
    );
  }

  return (
    <span className="mcq-target-editable" onClick={() => { setDraft(value); setEditing(true); }} title="Edit target">
      {value}<span className="mcq-target-edit-icon">edit</span>
    </span>
  );
}

export default function MCQ() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/mcq').then(data => { setWeeks(data); setLoading(false); });
  }, []);

  const updateCount = async (weekNum, day, val) => {
    const count = Math.max(0, parseInt(val) || 0);
    setWeeks(prev => prev.map(w => {
      if (w.week_number !== weekNum) return w;
      const days = { ...w.days, [day]: count };
      return { ...w, days, actual: Object.values(days).reduce((s, x) => s + x, 0) };
    }));
    await api('/api/mcq', { method: 'POST', body: JSON.stringify({ week_number: weekNum, day, count }) });
  };

  const updateTarget = (weekNum, newTarget) => {
    setWeeks(prev => prev.map(w => w.week_number === weekNum ? { ...w, target: newTarget } : w));
  };

  if (loading) return <div className="loading">Loading</div>;

  const totalDone = weeks.reduce((s, w) => s + w.actual, 0);
  const totalTarget = weeks.reduce((s, w) => s + w.target, 0);
  const pct = totalTarget > 0 ? Math.round((totalDone / totalTarget) * 100) : 0;

  return (
    <div className="mcq-page">
      <h1 className="page-heading">MCQ log</h1>
      {totalDone === 0 && (
        <div className="mcq-empty-state">Your weekly MCQ counts will appear here. Enter the number of questions you complete each day.</div>
      )}
      <div className="mcq-summary">
        <div className="mcq-total">{totalDone.toLocaleString()}</div>
        <div className="mcq-total-label">of {totalTarget.toLocaleString()} target MCQs <InfoTooltip text="Your target number of MCQ questions per day. Set in Settings. The weekly and overall targets are calculated from this." /></div>
        <div className="mcq-overall-bar"><div className="mcq-overall-fill" style={{ width: pct + '%' }} /></div>
        <div className="mcq-pct">{pct}%</div>
      </div>

      <div className="mcq-target-hint">Select any target number to edit it for that week.</div>

      {/* Desktop table */}
      <div className="mcq-table-wrap mcq-desktop">
        <table className="mcq-table">
          <thead>
            <tr>
              <th style={{ textAlign:'left', paddingLeft:4 }}>Week</th>
              <th style={{ textAlign:'left' }}>Dates</th>
              <th>Target</th>
              {DAYS.map(d => <th key={d}>{d}</th>)}
              <th>Total <InfoTooltip text="Total questions logged this week across all days." /></th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map(w => {
              const wpct = w.target > 0 ? Math.min(100, Math.round((w.actual / w.target) * 100)) : 0;
              const isOver = w.actual >= w.target && w.target > 0;
              return (
                <tr key={w.week_number} className={isOver ? 'mcq-row-done' : ''}>
                  <td className="mcq-week-cell">W{w.week_number}</td>
                  <td className="mcq-date-cell">{getWeekDateRange(w.week_number)}</td>
                  <td className="mcq-target-cell" title="Edit target">
                    <EditableTarget weekNum={w.week_number} value={w.target} onChange={n => updateTarget(w.week_number, n)} />
                  </td>
                  {DAYS.map(d => (
                    <td key={d} className="mcq-day-cell">
                      <input className="mcq-input" type="number" min="0" placeholder="—"
                        value={w.days[d] || ''} onChange={e => updateCount(w.week_number, d, e.target.value)} />
                    </td>
                  ))}
                  <td className="mcq-total-cell" style={{ color: isOver ? 'var(--green)' : 'var(--text)' }}>{w.actual}</td>
                  <td className="mcq-pct-cell" style={{ color: wpct>=100?'var(--green)':wpct>50?'var(--amber)':'var(--muted)' }}>{wpct}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card layout */}
      <div className="mcq-mobile-cards">
        {weeks.map(w => {
          const wpct = w.target > 0 ? Math.min(100, Math.round((w.actual / w.target) * 100)) : 0;
          const isOver = w.actual >= w.target && w.target > 0;
          return (
            <div key={w.week_number} className={`mcq-card${isOver ? ' mcq-card-done' : ''}`}>
              <div className="mcq-card-header">
                <span className="mcq-card-week">W{w.week_number}</span>
                <span className="mcq-card-dates">{getWeekDateRange(w.week_number)}</span>
                <span className="mcq-card-target">Target: <EditableTarget weekNum={w.week_number} value={w.target} onChange={n => updateTarget(w.week_number, n)} /></span>
              </div>
              <div className="mcq-card-days">
                {DAYS.map(d => (
                  <div key={d} className="mcq-card-day">
                    <label className="mcq-card-day-label">{d}</label>
                    <input className="mcq-input" type="number" min="0" placeholder="—"
                      value={w.days[d] || ''} onChange={e => updateCount(w.week_number, d, e.target.value)} />
                  </div>
                ))}
              </div>
              <div className="mcq-card-footer">
                <span style={{ color: isOver ? 'var(--green)' : 'var(--text)', fontWeight: 600 }}>Total: {w.actual}</span>
                <span style={{ color: wpct>=100?'var(--green)':wpct>50?'var(--amber)':'var(--muted)', fontFamily: 'var(--mono)' }}>{wpct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
